// src/pages/Reports.js
//
// SECTION 533 (2026-09-30, Bob: "What about reports from the platform about claims, discipline ..."). The
// officer's reports: claims status, deadlines, money, rule trends, discipline, activity. READ-ONLY - every
// number is counted from what this officer can already read (the same shaped queries his desk and the
// Discipline page run; the rules decide). Each table downloads as CSV; the page prints. Every rule of what
// is counted and how is src\data\reports.js's; this page draws.
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { subscribeVisibleCases } from '../data/disciplineQueries';   // ruling 56 (Record 577)
import { subscribeVisibleClaims } from '../data/claimQueries';
import { LC_ROSTER } from '../data/lcRoster';
import { money } from '../data/claimTally';
import { REPORTS, statusReport, deadlineReport, moneyReport, ruleReport, disciplineReport, activityReport, toCsv, today } from '../data/reports';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2', head: '#10233A' };
const seatLabel = (k) => { const r = LC_ROSTER.find((x) => x.scopeKey === k); return r ? r.label.replace(/\s+\(.*\)$/, '') : k; };
const th = { textAlign: 'left', padding: '6px 8px', fontSize: 11, color: C.dim, borderBottom: '1px solid ' + C.line, background: C.head, whiteSpace: 'nowrap' };
const td = { padding: '5px 8px', fontSize: 12, color: C.text, borderBottom: '1px solid ' + C.line, verticalAlign: 'top' };
const num = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

function download(name, header, rows) {
  try {
    const blob = new Blob([toCsv(header, rows)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = name; document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
  } catch (e) { /* the table is still on the screen */ }
}

function Table({ title, note, header, rows, csv, right }) {
  const r = right || [];
  return (
    <div style={{ background: C.panel, border: '1px solid ' + C.line, borderRadius: 8, marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, padding: '10px 12px', borderBottom: '1px solid ' + C.line }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{title}</div>
          {note ? <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{note}</div> : null}
        </div>
        {csv && rows.length ? <button className="btn btn-secondary btn-sm no-print" onClick={() => download(csv, header, rows)}>Download CSV</button> : null}
      </div>
      {rows.length === 0 ? <div style={{ padding: 12, fontSize: 12, color: C.dim }}>Nothing to report.</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{header.map((h, i) => <th key={i} style={{ ...th, textAlign: r.includes(i) ? 'right' : 'left' }}>{h}</th>)}</tr></thead>
            <tbody>{rows.map((row, i) => <tr key={i}>{row.map((v, j) => <td key={j} style={r.includes(j) ? num : td}>{v}</td>)}</tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Tiles({ items }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
      {items.map((x, i) => (
        <div key={i} style={{ background: C.panel, border: '1px solid ' + (x.tone === 'bad' ? C.bad : x.tone === 'warn' ? C.accent : C.line), borderRadius: 8, padding: '10px 14px', minWidth: 140 }}>
          <div style={{ fontSize: 11, color: C.dim }}>{x.label}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: x.tone === 'bad' ? C.bad : x.tone === 'warn' ? C.accent : C.text, fontVariantNumeric: 'tabular-nums' }}>{x.value}</div>
          {x.sub ? <div style={{ fontSize: 11, color: C.dim }}>{x.sub}</div> : null}
        </div>
      ))}
    </div>
  );
}

function uiRole(user) {
  const r = user && user.role;
  if (r === 'admin' || (user && user.isAdmin)) return 'admin';
  if (r === 'general_chair') return 'gc';
  if (r === 'local_chair') return 'lc';
  return 'member';
}

export default function Reports({ user }) {
  const [tab, setTab] = useState('status');
  const [claims, setClaims] = useState(null);
  const [cases, setCases] = useState(null);
  const [err, setErr] = useState('');
  const t = today();
  const uid = user && user.uid;
  const committee = user && user.committee;
  const scopesKey = ((user && user.scopes) || []).join('|');

  useEffect(() => {
    if (!user) return undefined;
    return subscribeVisibleClaims(db, user, rows => setClaims(rows), e => { setErr('Claims could not be read: ' + (e && e.code ? e.code : e.message)); setClaims([]); });
  }, [uid, committee, scopesKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // The same query the Discipline page runs (F-11): what the read rule can prove for this officer.
    if (!user) return undefined;
    const r = uiRole(user);
    void r;
    return subscribeVisibleCases(db, user, onSnapshot, rows => setCases(rows),
      e => { setErr('Discipline cases could not be read: ' + (e && e.code ? e.code : e.message)); setCases([]); });
  }, [uid, committee, scopesKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  const opts = { today: t, seatLabel };
  const R = useMemo(() => (claims && cases) ? {
    status: statusReport(claims, opts), deadlines: deadlineReport(claims, cases, opts), money: moneyReport(claims, opts),
    rules: ruleReport(claims, opts), discipline: disciplineReport(cases, opts), activity: activityReport(claims, cases, opts),
  } : null, [claims, cases, t]);

  const scope = uiRole(user) === 'gc' ? (user.committee || 'your committee') : uiRole(user) === 'lc' ? 'your territory' : uiRole(user) === 'admin' ? 'every committee' : 'your own records';
  const stamp = 'as of ' + t;

  let body = null;
  if (!R) body = <div style={{ color: C.dim, fontSize: 13 }}>Reading the claims and cases…</div>;
  else if (tab === 'status') {
    const s = R.status;
    body = <>
      <Tiles items={[{ label: 'Filed claims', value: s.total }, { label: 'Open', value: s.open, tone: s.open ? 'warn' : null }, { label: 'Open over 90 days', value: s.ages.filter(a => a.key === '91-180' || a.key === 'over 180').reduce((n, a) => n + a.n, 0), tone: 'bad' }, { label: 'Open with no date', value: s.undated, sub: 'no presented or filed date on the claim' }]} />
      <Table title="Where the claims stand" note={'Every filed claim by rung of the ladder, ' + stamp + '. Drafts are the author\'s alone and are not counted.'} csv={'claims-status-' + t + '.csv'} right={[1]}
        header={['Stage', 'Claims']} rows={s.stages.map(x => [x.label, x.n]).concat(s.other.map(x => [x.key + ' (off the ladder)', x.n]))} />
      <Table title="By office" csv={'claims-by-office-' + t + '.csv'} right={[1, 2]} header={['Office', 'Filed', 'Open']} rows={s.offices.map(o => [o.label, o.n, o.open])} />
      <Table title="How long open claims have been open" note="Days since the claim was presented (or filed, when no presented date is on it)." csv={'claims-age-' + t + '.csv'} right={[1]} header={['Days open', 'Claims']} rows={s.ages.map(a => [a.key, a.n])} />
      <Table title="Oldest open claims" csv={'claims-oldest-' + t + '.csv'} right={[5]} header={['Claim', 'Claimant', 'Stage', 'Office', 'Filed', 'Days']} rows={s.oldest.map(o => [o.label, o.claimant, o.status, o.office, o.filedOn, o.age])} />
    </>;
  } else if (tab === 'deadlines') {
    const d = R.deadlines;
    body = <>
      <Tiles items={[{ label: 'Overdue', value: d.overdue, tone: d.overdue ? 'bad' : null }, { label: 'Next 7 days', value: d.week, tone: d.week ? 'warn' : null }, { label: 'Next ' + d.horizon + ' days', value: d.rows.filter(r => r.days >= 0).length }]} />
      <Table title="Deadlines" note={'Claim filing windows and carrier answers from the agreement on each claim; discipline investigation deadlines and scheduled hearings. Overdue items show a negative day count. ' + stamp + '.'}
        csv={'deadlines-' + t + '.csv'} right={[1]} header={['Date', 'Days', 'Source', 'What']}
        rows={d.rows.map(r => [r.date, r.days < 0 ? r.days + ' OVERDUE' : r.days, r.source, r.title])} />
    </>;
  } else if (tab === 'money') {
    const m = R.money; const x = m.total;
    body = <>
      <Tiles items={[{ label: 'Claimed', value: money(x.submitted.amount), sub: x.submitted.n + ' claims' + (x.submitted.noAmount ? ', ' + x.submitted.noAmount + ' with no amount' : '') }, { label: 'Recovered (documented)', value: money(x.awarded.amount), sub: x.awarded.n + ' won' + (x.awarded.unsourcedOn ? ', ' + money(x.awarded.unsourced) + ' undocumented not added' : '') }, { label: 'Lost', value: money(x.unsuccessful.amount), sub: x.unsuccessful.n + ' claims' }, { label: 'Open / at a Board', value: x.open.n + ' / ' + x.board.n }]} />
      <Table title="Money by office" note="Recovered money counts only with a document or pay record behind it; a claim with no amount is counted, never as $0. [R-161]" csv={'money-by-office-' + t + '.csv'} right={[1, 2, 3, 4, 5, 6]}
        header={['Office', 'Filed', 'Claimed', 'Won', 'Recovered', 'Lost', 'Lost amount']}
        rows={m.offices.map(o => [o.label, o.tally.submitted.n, money(o.tally.submitted.amount), o.tally.awarded.n, money(o.tally.awarded.amount), o.tally.unsuccessful.n, money(o.tally.unsuccessful.amount)])} />
    </>;
  } else if (tab === 'rules') {
    const r = R.rules;
    body = <>
      <Table title="Rules claimed most" note={'The rule(s) each filed claim rests on, as the claim names them. ' + (r.none ? r.none + ' claim(s) name no rule. ' : '') + (r.more ? r.more + ' more rule(s) not shown; the CSV has the top list only. ' : '') + stamp + '.'}
        csv={'rule-trends-' + t + '.csv'} right={[1, 2, 3, 4]} header={['Rule', 'Claims', 'Won', 'Lost', 'Open']} rows={r.rules.map(x => [x.rule, x.n, x.won, x.lost, x.open])} />
      <Table title="Last twelve months, by rule" csv={'rule-months-' + t + '.csv'} right={r.months.map((_, i) => i + 1)}
        header={['Rule'].concat(r.months.map(m => m.slice(2)))} rows={r.rules.map(x => [x.rule].concat(r.months.map(m => x.byMonth[m] || 0)))} />
    </>;
  } else if (tab === 'discipline') {
    const k = R.discipline;
    body = <>
      <Tiles items={[{ label: 'Cases', value: k.total }, { label: 'Open', value: k.open, tone: k.open ? 'warn' : null }, { label: 'Hearings coming', value: k.hearings.length }, { label: 'Preservation demands not issued', value: k.preservationGaps, tone: k.preservationGaps ? 'bad' : null, sub: 'radio, phone, video [R-158]' }]} />
      <Table title="Cases by stage" csv={'discipline-stages-' + t + '.csv'} right={[1]} header={['Stage', 'Cases']} rows={k.stages.map(x => [x.label, x.n]).concat(k.other.map(x => [x.key, x.n]))} />
      <Table title="Hearings coming" csv={'discipline-hearings-' + t + '.csv'} right={[1]} header={['Date', 'Days', 'Employee', 'Office']} rows={k.hearings.map(h => [h.date, h.days, h.who, h.office])} />
      <Table title="Preservation demands not yet issued" note="Open cases where the demand to preserve radio, phone or video has not gone out and is not waived." csv={'discipline-preservation-' + t + '.csv'} header={['Employee', 'Not issued']} rows={k.gaps.map(g => [g.who, g.unissued.join(', ')])} />
    </>;
  } else if (tab === 'activity') {
    const a = R.activity; const mm = a.months;
    body = <>
      <Table title="Claims and cases per month" note={'Claims by the month presented (or filed); cases by notice date (or opened); outcomes by the month recorded. ' + stamp + '.'}
        csv={'activity-' + t + '.csv'} right={[1, 2, 3]} header={['Month', 'Claims filed', 'Outcomes recorded', 'Discipline cases']}
        rows={mm.map(m => [m, a.claimsByMonth[m], a.outcomesByMonth[m], a.casesByMonth[m]])} />
      <Table title="Claims filed by office, last twelve months" csv={'activity-by-office-' + t + '.csv'} right={mm.map((_, i) => i + 1).concat([mm.length + 1])}
        header={['Office'].concat(mm.map(m => m.slice(2))).concat(['Total'])} rows={a.offices.map(o => [o.label].concat(mm.map(m => o.byMonth[m])).concat([o.n]))} />
    </>;
  }

  return (
    <div style={{ padding: 20 }}>
      <style>{'@media print { .no-print { display: none !important; } }'}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h2 style={{ margin: 0, color: C.text, fontSize: 20 }}>Reports</h2>
          <div style={{ fontSize: 12, color: C.dim, marginTop: 4 }}>Counted from the claims and discipline cases you can already read - {scope}, {stamp}. Read-only; nothing here changes a record.</div>
        </div>
        <button className="btn btn-secondary btn-sm no-print" onClick={() => { try { window.print(); } catch (e) { /* no printer */ } }}>Print</button>
      </div>
      <div className="no-print" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {REPORTS.map(r => <button key={r.key} className={'btn btn-sm ' + (tab === r.key ? 'btn-primary' : 'btn-secondary')} onClick={() => setTab(r.key)}>{r.label}</button>)}
      </div>
      {err ? <div style={{ color: C.bad, fontSize: 12, marginBottom: 12 }}>&#9888; {err}</div> : null}
      {body}
    </div>
  );
}
